/**
 * The Monitor banner (DEC-187 decision 2, UI_SDD §R.3).
 *
 * One card that follows the scan. Top: a status dot (pulsing while running), a headline
 * and a one-line subtitle, with Agents, Progress, Run time and Tokens on the right. Below:
 * today's segmented severity bar, unchanged in shape, size and behaviour (the founder's
 * choice: "that is the king"). For a finished scan, under a hairline inside the same card:
 * findings, debt carried, to remediate, the Critic line and the three actions. This
 * replaces the separate completion box and DEC-185's separate summary line.
 *
 * Inline stop confirmation (5s auto-dismiss) and the budget banner are unchanged.
 *
 * DEC-195: "Run time" replaced "Elapsed". It excludes every wait for a person, so it
 * freezes while the scan waits and says so ("waiting for you"). Paused time is never shown.
 */
import { useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ArrowRight, Gauge, Pause, Play, Square } from 'lucide-react'
import { getExecutiveReport } from '../../api/executive'
import type { SeverityCounts } from '../../stores/scanStore'
import type { BudgetWarningEvent, CriticEvent, ScanState } from '../../types/api'
import { compactNumber, dollars } from '../../utils/format'
import { tokenColor } from './waveModel'

const STATE_MARK: Record<string, string> = {
  queued: 'mark-idle',
  cloning: 'mark-running',
  planning: 'mark-running',
  running: 'mark-running',
  critic: 'mark-running',
  paused: 'mark-warn',
  complete: 'mark-success',
  partial: 'mark-high',
  failed: 'mark-critical',
}

interface StatusBarProps {
  state: ScanState | null
  /** "Scanning · Wave 3 of 4", "Scan complete · 15 findings, 4 High" (built by the page). */
  headline: string
  /** "Architecture is running now · 8 of 13 agents finished" / "Finished ... · took ..." */
  subtitle: string
  /** DEC-185: a saved scan's "after review" note, inside the banner. */
  note?: ReactNode
  /** Agents counted the same way as the subtitle: skipped counts as finished. */
  agentsDone: number
  agentsTotal: number
  progress: number
  /** DEC-195: "6m 12s", "at least 9m 03s" or "not recorded", built by the page. */
  runTime: string
  /** DEC-195: the scan waits for a person, so the run time is not moving. */
  waitingForPerson?: boolean
  tokensUsed: number
  severityCounts: SeverityCounts
  budgetWarning: BudgetWarningEvent | null
  tokenBudget: number | null
  isActive: boolean
  isPaused: boolean
  onPause: () => void
  onResume: () => void
  onStopRequest: () => void
  stopConfirmOpen: boolean
  onStopConfirm: () => void
  onStopDismiss: () => void
  pausePending: boolean
  resumePending: boolean
  /** Shown in place of the severity bar while there are no findings. */
  emptySeverityText?: string
  /** Finished scans only: the results section under the hairline. */
  results?: ReactNode
}

export function StatusBar({
  state,
  headline,
  subtitle,
  note,
  agentsDone,
  agentsTotal,
  progress,
  runTime,
  waitingForPerson = false,
  tokensUsed,
  severityCounts,
  budgetWarning,
  tokenBudget,
  isActive,
  isPaused,
  onPause,
  onResume,
  onStopRequest,
  stopConfirmOpen,
  onStopConfirm,
  onStopDismiss,
  pausePending,
  resumePending,
  emptySeverityText = 'Awaiting findings…',
  results,
}: StatusBarProps) {
  // Inline stop confirmation auto-dismisses after 5s (§R.6 rule 6)
  useEffect(() => {
    if (!stopConfirmOpen) return
    const t = setTimeout(onStopDismiss, 5000)
    return () => clearTimeout(t)
  }, [stopConfirmOpen, onStopDismiss])

  const stateKey = state ?? 'queued'
  const markToken = STATE_MARK[stateKey] ?? 'mark-idle'
  const total = severityCounts.critical + severityCounts.high + severityCounts.medium + severityCounts.low

  const segments = [
    // Solid fills with their own text colour: the number sits ON the colour, so
    // this is one of the few solid uses left (DEC-193).
    { key: 'critical', count: severityCounts.critical, bg: 'bg-badge-critical', text: 'text-on-critical', sub: 'text-on-critical/75' },
    { key: 'high', count: severityCounts.high, bg: 'bg-badge-high', text: 'text-on-high', sub: 'text-on-high/75' },
    { key: 'medium', count: severityCounts.medium, bg: 'bg-badge-medium', text: 'text-on-medium', sub: 'text-on-medium/75' },
    { key: 'low', count: severityCounts.low, bg: 'bg-badge-low', text: 'text-on-low', sub: 'text-on-low/75' },
  ]

  const stats: { label: string; id: string; value: string; tone: string; note?: string }[] = [
    { label: 'Agents', id: 'agents', value: `${agentsDone} of ${agentsTotal}`, tone: 'text-ink' },
    { label: 'Progress', id: 'progress', value: `${progress}%`, tone: 'text-ink' },
    {
      label: 'Run time',
      id: 'runtime',
      value: runTime,
      tone: runTime === 'not recorded' ? 'text-muted' : 'text-ink',
      note: waitingForPerson ? 'waiting for you' : undefined,
    },
    { label: 'Tokens', id: 'tokens', value: tokensUsed > 0 ? compactNumber(tokensUsed) : 'none', tone: tokensUsed > 0 ? 'text-ink' : 'text-muted' },
  ]

  return (
    <div
      data-testid="monitor-banner"
      className="bg-panel border rounded-lg shadow-card px-[22px] py-5 mb-[14px] flex flex-col gap-4"
      style={{
        // Founder, 2026-10-12: a finished scan's banner is plain (no green edge or glow). Only a
        // live scan keeps its violet edge and glow, so "something is running" stays obvious.
        borderColor: isActive || isPaused ? tokenColor(markToken, 0.35) : 'var(--hairline)',
        backgroundImage: isActive ? `radial-gradient(120% 160% at 100% 0%, ${tokenColor(markToken, 0.14)}, transparent 55%)` : undefined,
      }}
    >
      {/* Top: dot + headline + subtitle on the left, stats and controls on the right */}
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div className="flex flex-col gap-[6px] min-w-0">
          <span className="inline-flex items-center gap-[10px]">
            <span
              data-testid="banner-dot"
              className={`w-[10px] h-[10px] rounded-full flex-none ${isActive ? 'animate-pulse' : ''}`}
              style={{ background: tokenColor(markToken), boxShadow: `0 0 0 4px ${tokenColor(markToken, 0.22)}` }}
            />
            <span data-testid="banner-headline" className="font-semibold text-[20px] leading-tight tracking-[-0.01em] text-ink">
              {headline}
            </span>
          </span>
          <span data-testid="banner-subtitle" className="text-[13px] leading-[1.45] text-body pl-5">
            {subtitle}
          </span>
          {note && <div className="text-[11.5px] leading-[1.5] text-muted pl-5">{note}</div>}
        </div>
        <div className="flex items-start gap-[22px] flex-wrap">
          {stats.map((s) => (
            <div key={s.label} className="flex flex-col gap-1 items-end">
              <span className="font-semibold text-[10px] leading-none tracking-[0.16em] uppercase text-muted">{s.label}</span>
              <span data-testid={`banner-stat-${s.id}`} className={`font-medium tabular-nums text-[16px] leading-none ${s.tone}`}>
                {s.value}
              </span>
              {s.note && (
                <span data-testid={`banner-stat-${s.id}-note`} className="text-[11px] leading-none text-warn">
                  {s.note}
                </span>
              )}
            </div>
          ))}
          {(isActive || isPaused) && (
            <div className="flex items-center gap-[6px] self-center">
              <button
                onClick={isPaused ? onResume : onPause}
                disabled={pausePending || resumePending}
                className="flex items-center gap-[5px] font-sans font-semibold text-[11px] text-ink bg-ground border border-hairline rounded-md px-3 py-[6px] hover:border-hover-edge transition-colors disabled:opacity-50"
              >
                {isPaused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
                {isPaused ? (resumePending ? 'Resuming…' : 'Resume') : pausePending ? 'Pausing…' : 'Pause'}
              </button>
              <button
                onClick={onStopRequest}
                className="flex items-center gap-[5px] font-sans font-semibold text-[11px] text-critical bg-chip-critical border border-critical/30 rounded-md px-3 py-[6px] hover:bg-chip-critical transition-colors"
              >
                <Square className="w-[11px] h-[11px]" />
                Stop
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Segmented severity bar: today's, unchanged (DEC-187 decision 2) */}
      {total > 0 ? (
        <div data-testid="severity-bar" className="flex gap-[2px] rounded-lg overflow-hidden h-[76px] bg-ground">
          {segments.map((s) => (
            <div
              key={s.key}
              className={`${s.bg} px-3 py-[10px] flex flex-col justify-end overflow-hidden min-w-0 transition-[flex] duration-500`}
              style={{ flex: Math.max(s.count, 0.35) }}
            >
              <div className={`font-semibold tabular-nums text-[22px] leading-none whitespace-nowrap ${s.text}`}>{s.count}</div>
              <div className={`font-semibold text-[7px] leading-none tracking-[2.5px] uppercase mt-[5px] whitespace-nowrap ${s.sub}`}>
                {s.key}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="h-[76px] rounded-lg bg-ground flex items-center px-4">
          <span className="font-medium text-[11px] leading-none text-faint tracking-[0.3px]">{emptySeverityText}</span>
        </div>
      )}

      {results}

      {/* Inline stop confirmation */}
      {stopConfirmOpen && (
        <div className="flex items-center gap-3 px-[14px] py-[10px] bg-chip-critical border border-critical/30 rounded-md">
          <AlertTriangle className="w-[15px] h-[15px] text-critical flex-none" />
          <span className="font-medium text-[12px] leading-[1.4] text-body flex-1">
            Stopping ends the scan and discards all progress. This can't be undone.
          </span>
          <button
            onClick={onStopConfirm}
            className="font-semibold text-[11px] text-white bg-critical rounded-md px-[14px] py-[7px] hover:opacity-90"
          >
            Stop scan
          </button>
          <button
            onClick={onStopDismiss}
            className="font-semibold text-[11px] text-body bg-ground border border-hairline rounded-md px-[14px] py-[7px]"
          >
            Keep running
          </button>
        </div>
      )}

      {/* Budget banner */}
      {budgetWarning && (
        <div className="flex items-center gap-[10px] px-[14px] py-[9px] bg-chip-high border border-high/40 rounded-md">
          <Gauge className="w-[15px] h-[15px] text-high flex-none" />
          <span className="font-medium text-[12px] leading-[1.4] text-warn flex-1">
            Token budget at {budgetWarning.percent}% ({compactNumber(budgetWarning.tokens_used)}
            {tokenBudget ? ` / ${compactNumber(tokenBudget)}` : ''}).
            {budgetWarning.action === 'skip_llm' && ' LLM passes on remaining agents are being skipped.'}
            {budgetWarning.action === 'abort' && ' The scan is aborting.'}
            {budgetWarning.action === 'warn' && ' LLM passes on remaining agents will be skipped if the cap is reached.'}
          </span>
        </div>
      )}
    </div>
  )
}

interface BannerResultsProps {
  scanId: string
  state: ScanState
  findingCount: number
  agentsFailedCount: number
  criticSummary: CriticEvent | null
}

/**
 * The finished scan's results, under a hairline inside the banner (DEC-187 decision 2).
 * Same data the old completion box showed: DEC-059 mapping, expected (confidence-weighted)
 * debt and the remediation total. No auto-navigate (§R.6 rule 2).
 */
export function BannerResults({ scanId, state, findingCount, agentsFailedCount, criticSummary }: BannerResultsProps) {
  const navigate = useNavigate()
  const isPartial = state === 'partial'

  // Metrics only: narrative generation is deferred to the Executive page.
  const { data: metrics } = useQuery({
    queryKey: ['executive-metrics-lite', scanId],
    queryFn: () => getExecutiveReport(scanId, 5, false, false),
    staleTime: 60_000,
  })

  const criticLine = criticSummary
    ? `Critic consolidated ${criticSummary.total_findings_before} raw findings into ${criticSummary.total_findings_after} · ${criticSummary.groups_created} groups · ${criticSummary.findings_suppressed} suppressed`
    : isPartial && agentsFailedCount > 0
      ? `${agentsFailedCount} agent${agentsFailedCount === 1 ? '' : 's'} failed. Click one in the wave rows below to see why.`
      : null

  return (
    <div data-testid="banner-results" className="border-t border-hairline pt-4 flex flex-col gap-3">
      <div className="flex justify-between items-center gap-[18px] flex-wrap">
        <div className="flex gap-8 flex-wrap">
          <div>
            <div className="font-medium tabular-nums text-[24px] leading-none tracking-tight text-ink">{findingCount}</div>
            <div className="text-[11px] text-muted mt-[6px]">findings</div>
          </div>
          {metrics && (
            <>
              <div>
                <div className="font-medium tabular-nums text-[24px] leading-none tracking-tight text-critical">
                  {dollars(metrics.expected_debt_cost_dollars)}
                </div>
                <div className="text-[11px] text-muted mt-[6px]">debt carried, confidence-weighted</div>
              </div>
              <div>
                <div className="font-medium tabular-nums text-[24px] leading-none tracking-tight text-success">
                  {dollars(metrics.total_debt_cost_dollars)}
                </div>
                <div className="text-[11px] text-muted mt-[6px]">to remediate</div>
              </div>
            </>
          )}
        </div>
        <div className="flex gap-[10px] flex-wrap">
          <button
            onClick={() => navigate(`/scans/${scanId}/findings`)}
            className="flex items-center gap-[7px] font-semibold text-[12px] text-white bg-accent rounded-md px-[18px] py-[10px] hover:opacity-90 transition-opacity"
          >
            View findings <ArrowRight className="w-[14px] h-[14px]" />
          </button>
          <button
            onClick={() => navigate(`/scans/${scanId}/executive-report`)}
            className="font-semibold text-[12px] text-accent-text bg-panel border border-accent/30 rounded-md px-[18px] py-[10px] hover:bg-hover-hue transition-colors"
          >
            Executive report
          </button>
          <button
            onClick={() => navigate('/connect')}
            className="font-semibold text-[12px] text-body bg-ground border border-hairline rounded-md px-[18px] py-[10px] hover:bg-hover-hue transition-colors"
          >
            New scan
          </button>
        </div>
      </div>
      {criticLine && (
        <div data-testid="banner-critic" className="text-[11.5px] leading-[1.4] text-muted">
          {criticLine}
        </div>
      )}
    </div>
  )
}
